import { ApolloProvider } from "@apollo/react-hooks";
import ApolloClient from "apollo-boost";
import {
  InMemoryCache,
  IntrospectionFragmentMatcher,
} from "apollo-cache-inmemory";
import Amplify from "aws-amplify";
import React from "react";
import { SyncLoader } from "react-spinners";
import { animated, useSpring } from "react-spring";
import styled, { ThemeProvider } from "styled-components";

import aws_exports from "./aws-exports";
import Header from "./components/header/Header";
import Panel from "./components/panel/Panel";
import Text from "./components/shared/Text";
import InteractiveMap from "./containers/InteractiveMap";
import { initialLoadingState, LoadingContext } from "./contexts/LoadingContent";
import { initialPanelState, PanelContext } from "./contexts/PanelContext";
import { initialPinState, PinContext } from "./contexts/PinContext";
import introspectionResult from "./generated/introspection-result";
import { useWindowSize } from "./hooks/useWindowSize";
import { GlobalStyle, theme } from "./utils/theme";
import * as Sentry from "@sentry/browser";
import TagManager from 'react-gtm-module'

Sentry.init({
  dsn:
    "https://cfd6c248028c4e92bd8078f332e6669d@o374901.ingest.sentry.io/5193593",
});

const tagManagerArgs = {
  gtmId: 'GTM-T74V2Z6'
}

TagManager.initialize(tagManagerArgs)

const App: React.FC = () => {
  const fragmentMatcher = new IntrospectionFragmentMatcher({
    introspectionQueryResultData: introspectionResult,
  });

  const cache = new InMemoryCache({
    fragmentMatcher,
  });

  const client = new ApolloClient({
    uri: "https://voe.gravitywell-hub.co.uk/graphql",
    cache,
  });

  Amplify.configure(aws_exports);

  const panelHook = React.useState(initialPanelState);
  const pinHook = React.useState(initialPinState);
  const loadingHook = React.useState(initialLoadingState);
  const [
    { backgroundLoaded, pinsLoaded, error, isReady },
    setLoadedState,
  ] = loadingHook;

  // Watch for window size changes, set --vh css var
  useWindowSize();

  // On mount
  React.useEffect(() => {
    if (backgroundLoaded && pinsLoaded && !error) {
      setTimeout(() => {
        setLoadedState({
          backgroundLoaded: true,
          pinsLoaded: true,
          error: null,
          isCartoucheComplete: false,
          isReady: true,
        });
      }, 1000);
    }
  }, [backgroundLoaded, pinsLoaded, error, setLoadedState]);

  const styles = useSpring({
    from: { opacity: 1 },
    opacity: isReady ? 0 : 1,
  });

  return (
    <MapWrapper>
      <GlobalStyle />
      <ThemeProvider theme={theme}>
        <ApolloProvider client={client}>
          <LoadingContext.Provider value={loadingHook}>
            <PanelContext.Provider value={panelHook}>
              <PinContext.Provider value={pinHook}>
                <Header />
                <InteractiveMap />
                <Panel />
                {error && (
                  <ErrorWrapper>
                    <Text as="h3">Oops!</Text>
                    <Text as="p">
                      There was an error. Please try again later.
                    </Text>
                    <Text as="p" small={true}>
                      {error}
                    </Text>
                  </ErrorWrapper>
                )}
                <LoadingWrapper
                  className={!isReady ? "show" : "hidden"}
                  style={styles}
                >
                  <SyncLoader color={theme.colors.accent} />
                </LoadingWrapper>
              </PinContext.Provider>
            </PanelContext.Provider>
          </LoadingContext.Provider>
        </ApolloProvider>
      </ThemeProvider>
    </MapWrapper>
  );
};

const LoadingWrapper = styled(animated.div as any)`
  z-index: 100;
  position: fixed;
  width: 100%;
  height: 100vh;
  height: calc(var(--vh, 1vh) * 100);
  background: ${({ theme }) => theme.colors.bg};
  display: flex;
  justify-content: center;
  align-items: center;
  &.show {
    pointer-events: all;
  }
  &.hidden {
    pointer-events: none;
  }
`;

const ErrorWrapper = styled.div`
  z-index: 100;
  position: fixed;
  width: 100%;
  height: 100vh;
  height: calc(var(--vh, 1vh) * 100);
  background: ${({ theme }) => theme.colors.bg};
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  * {
    padding: 1rem;
  }
`;

const MapWrapper = styled.div`
  position: relative;
  overscroll-behavior: none;
  background: #f4f4f4;
  height: 100vh;
  height: calc(var(--vh, 1vh) * 100);
  width: 100%;
  overflow: hidden;
`;

export default App;
